/**
 * The `disabled` option, as a class plus a page-level listener instead of the
 * element's own `disabled` attribute.
 *
 * **Why not `element.disabled`.** The Core themes own that attribute: on every
 * variant selection their scripts write it from the variant's stock
 * (`button.attr('disabled', 'disabled')` for "Sin stock",
 * `.prop('disabled', isDisabled)` in flex), so a value written by the SDK is
 * undone by the next click. Restoring it is worse: the pristine value could be
 * "enabled" from a variant that was in stock, and `reset` would then re-enable
 * a button the theme had since disabled for being out of stock. With a class
 * of its own the SDK never reads or writes the theme's state, so both survive
 * each other.
 *
 * What the class carries:
 * - **behaviour**: a capturing `click` / `submit` listener on the document
 *   stops the event before any handler of the theme sees it, so neither a
 *   click nor the form submission the button would start reaches the cart;
 * - **appearance**: dimmed, with a `not-allowed` cursor — the usual look of
 *   an unavailable control, since the theme's own `:disabled` styles do not
 *   apply to an element that is not disabled;
 * - **accessibility**: `aria-disabled="true"`, put back as it was afterwards.
 *
 * @see ./handlers/README.md
 */

/** The class marking an element the SDK disabled. */
const DISABLED_CLASS = "js-nubesdk-disabled";

/** Marks the one `<style>` this module owns, so it is injected once. */
const STYLE_ELEMENT_ID = "nubesdk-customization-disabled";

/** A constant: no option value is ever interpolated into it. */
const STYLESHEET = `
.${DISABLED_CLASS} { opacity: 0.5 !important; cursor: not-allowed !important; }
`;

/**
 * The `aria-disabled` an element had before the SDK disabled it (`null` for
 * none), so enabling it gives the theme's own value back.
 */
const previousAria = new WeakMap<Element, string | null>();

let listening = false;

/**
 * Stop a click on, or a submission started by, an element the SDK disabled.
 * `submitter` covers the implicit submission too: Enter in the quantity field
 * submits through the form's default button, which is the add-to-cart one.
 */
function block(event: Event): void {
	const origin =
		event.type === "submit" ? (event as SubmitEvent).submitter : event.target;
	if (!(origin instanceof Element) || !origin.closest(`.${DISABLED_CLASS}`)) {
		return;
	}
	event.preventDefault();
	event.stopImmediatePropagation();
}

/**
 * Put the stylesheet and the listener on the page, once, and only when
 * something is actually disabled: a page where no app disables anything gets
 * neither.
 */
function ensureInstalled(): void {
	if (!listening) {
		// Capturing on the document runs before the theme's own handlers,
		// which are bound on the element or delegated from the document.
		document.addEventListener("click", block, true);
		document.addEventListener("submit", block, true);
		listening = true;
	}

	const head = document.head;
	// Never throws: `operation` is not wrapped, so a missing `<head>` has to
	// mean "not dimmed", not a `command_failed` on the wire.
	if (!head || document.getElementById(STYLE_ELEMENT_ID)) return;

	const style = document.createElement("style");
	style.id = STYLE_ELEMENT_ID;
	style.textContent = STYLESHEET;
	head.appendChild(style);
}

/**
 * Whether `element` is disabled *by the SDK* right now. Blind to the theme's
 * own `disabled` attribute, for the reason above: a virgin element reads
 * `false`, which is what `reset` restores.
 */
export function readDisabled(element: Element): boolean {
	return element.classList.contains(DISABLED_CLASS);
}

/** Disable `element`, or hand it back to the theme. Idempotent. */
export function writeDisabled(element: Element, disabled: boolean): void {
	if (disabled === readDisabled(element)) return;

	if (disabled) {
		ensureInstalled();
		previousAria.set(element, element.getAttribute("aria-disabled"));
		element.classList.add(DISABLED_CLASS);
		element.setAttribute("aria-disabled", "true");
		return;
	}

	element.classList.remove(DISABLED_CLASS);
	const aria = previousAria.get(element) ?? null;
	previousAria.delete(element);
	if (aria === null) element.removeAttribute("aria-disabled");
	else element.setAttribute("aria-disabled", aria);
}

/** Test-only: remove the listener so each test starts from a clean page. */
export function uninstallDisabling(): void {
	document.removeEventListener("click", block, true);
	document.removeEventListener("submit", block, true);
	listening = false;
}
